import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, Subscription, catchError, finalize, of, switchMap, tap } from 'rxjs';

import { AgoraSimulatorService } from 'src/app/services/agora-simulator.service';

import { RuntimeEnv, RuntimeEnvService } from 'src/app/services/runtime-env.service';

import { UiFeedbackService } from 'src/app/ui/ui-feedback.service';



@Component({

  standalone: false,

  selector: 'app-agora-simulator-control',

  templateUrl: './agora-simulator-control.component.html',

  styleUrls: ['./agora-simulator-control.component.css'],

})

export class AgoraSimulatorControlComponent implements OnInit, OnDestroy {

  _pendingProcess: any[] = [];
  readonly queueDataSource = new MatTableDataSource<any>([]);
  readonly pageSizeOptions = [5, 10, 25, 50];
  readonly defaultPageSize = 10;

  loadError: string | null = null;

  loading = false;

  selectedEnv: RuntimeEnv = 'QA';

  filterText = '';
  openActionMenuId: string | null = null;
  actionMenuPos = { top: 0, left: 0 };

  readonly displayedColumns = ['id', 'phone', 'document', 'amount', 'status', 'actions'];



  private readonly refreshRequest$ = new Subject<boolean>();

  private sub?: Subscription;

  private refreshPipelineSub?: Subscription;

  private pollTimer?: ReturnType<typeof setInterval>;

  private readonly pollMs = 30_000;
  private paginator?: MatPaginator;

  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator | undefined) {
    this.paginator = mp;
    this.queueDataSource.paginator = mp ?? null;
  }

  constructor(

    private simulaService: AgoraSimulatorService,

    private runtimeEnv: RuntimeEnvService,

    private feedback: UiFeedbackService,

    private cdr: ChangeDetectorRef,

  ) {}



  ngOnInit(): void {

    this.selectedEnv = this.runtimeEnv.get();

    this.refreshPipelineSub = this.refreshRequest$

      .pipe(

        switchMap((showLoading) => {

          const showSpinner = showLoading && this._pendingProcess.length === 0;

          if (showSpinner) {

            this.loading = true;

            this.cdr.detectChanges();

          }

          this.loadError = null;

          return this.simulaService.getPendingProcess().pipe(

            tap((response) => {

              this._pendingProcess = this.normalizeQueueResponse(response);
              this.syncQueueTable();
              this.loading = false;
              this.cdr.detectChanges();

            }),

            catchError((err) => {

              if (this._pendingProcess.length === 0) {

                this._pendingProcess = [];

              }

              const host = this.runtimeEnv.url('SIMULADOR_AGORA');

              this.loadError = `Error al consultar ${host}/queue: ${err?.status || err?.message || 'desconocido'}`;

              this.loading = false;

              this.cdr.detectChanges();

              return of(null);

            }),

            finalize(() => {

              this.loading = false;

              this.cdr.detectChanges();

            }),

          );

        }),

      )

      .subscribe();



    this.sub = this.runtimeEnv.env$.subscribe((env) => {

      this.selectedEnv = env;

      this.refresh(true);

    });



    this.pollTimer = setInterval(() => this.refresh(false), this.pollMs);

  }



  ngOnDestroy(): void {

    this.refreshPipelineSub?.unsubscribe();

    this.refreshRequest$.complete();

    this.sub?.unsubscribe();

    if (this.pollTimer) {

      clearInterval(this.pollTimer);

    }

  }



  get filteredProcesses(): any[] {

    const list = this._pendingProcess ?? [];

    const q = this.filterText.trim().toLowerCase();

    if (!q) {

      return list;

    }

    return list.filter((p) => {

      const id = String(p?.identiifier ?? '').toLowerCase();

      const phone = String(p?.requestData?.phone ?? '').toLowerCase();

      const doc = String(p?.requestData?.documentNumber ?? '').toLowerCase();

      return id.includes(q) || phone.includes(q) || doc.includes(q);

    });

  }



  onEnvChange(env: RuntimeEnv): void {

    this.runtimeEnv.set(env);

  }



  refresh(showLoading = true): void {
    this.refreshRequest$.next(showLoading);
  }

  onFilterChange(): void {
    this.syncQueueTable();
    this.paginator?.firstPage();
  }

  toggleActionMenu(identiifier: string, event: Event): void {
    event.stopPropagation();
    if (this.openActionMenuId === identiifier) {
      this.closeActionMenu();
      return;
    }
    const trigger = event.currentTarget as HTMLElement;
    const rect = trigger.getBoundingClientRect();
    const menuWidth = 192;
    this.actionMenuPos = {
      top: rect.bottom + 6,
      left: Math.max(8, rect.right - menuWidth),
    };
    this.openActionMenuId = identiifier;
    this.cdr.detectChanges();
  }

  closeActionMenu(): void {
    if (!this.openActionMenuId) {
      return;
    }
    this.openActionMenuId = null;
    this.cdr.detectChanges();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeActionMenu();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closeActionMenu();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.closeActionMenu();
  }

  private syncQueueTable(): void {
    this.queueDataSource.data = this.filteredProcesses;
  }

  private normalizeQueueResponse(response: unknown): any[] {

    if (Array.isArray(response)) {

      return response;

    }

    if (response && typeof response === 'object') {

      const data = (response as { data?: unknown }).data;

      if (Array.isArray(data)) {

        return data;

      }

    }

    return [];

  }



  ejecutarAutorizar(identifier: string): void {
    this.closeActionMenu();
    this.ejecutaAccion(identifier, 'AUTHORIZE');
  }

  ejecutarError98(identifier: string): void {
    this.closeActionMenu();
    this.ejecutaAccion(identifier, 'ERROR98');
  }



  ejecutaAccion(identifier: string, accion: string): void {

    this.simulaService.actionOnProcess(identifier, accion).subscribe({

      next: () => {

        this.feedback.toast(`Acción ${accion} enviada`);

        this.refresh();

      },

      error: (err) => {

        this.loadError = `Acción ${accion} falló: ${err?.status || err?.message || 'desconocido'}`;

        this.feedback.toast('Error al ejecutar acción');

        this.cdr.detectChanges();

      },

    });

  }



  truncateId(id: string): string {

    if (!id || id.length <= 12) {

      return id;

    }

    return `${id.slice(0, 8)}…`;

  }

}


